import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

import { useUserStore } from '@/stores/user-store'
import authService from '@/services/auth-service'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'home' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'home' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { roles: ['admin', 'user', 'organiser'] }, // only accissible to admin and user
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },

      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },

      {
        name: 'cart',
        path: 'cart',
        component: () => import('../pages/cart/CartPage.vue'),
        meta: { roles: ['user', 'admin', 'sa', 'organiser'] },
      },
      {
        name: 'checkout-success',
        path: 'checkout-success',
        component: () => import('../pages/cart/CheckoutSuccessPage.vue'),
      },
      {
        name: 'checkout',
        path: 'checkout',
        component: () => import('../pages/cart/CartPage.vue'),
        meta: { roles: ['user', 'admin', 'sa', 'organiser'] },
        beforeEnter: (to, from, next) => {
          // Create a new window for the redirect to avoid interfering with the current Vue Router navigation
          const newWindow = window.open('/api/v1/cart/checkout', '_blank')

          // If the new window opens successfully, send the Authorization header
          if (newWindow) {
            const authHeader = 'Bearer ' + authService.getAccessToken() // Replace with your actual token storage method
            newWindow.postMessage({ authHeader: authHeader }, '*')
          } else {
            // Handle the case where the new window could not be opened
            console.error('Failed to open new window for redirect.')
            // You might want to display an error message to the user here
          }
          // Prevent the default navigation to the page
          next(false)
        },
      },
      {
        name: 'home',
        path: 'home',
        component: () => import('../pages/events/ActiveEventsPage.vue'),
      },
      {
        name: 'events',
        path: 'events',
        component: () => import('../pages/events/ActiveEventsPage.vue'),
      },
      {
        name: 'my-events',
        path: 'my-events',
        component: () => import('../pages/events/MyEventsPage.vue'),
      },
      {
        name: 'all-events',
        path: 'all-events',
        component: () => import('../pages/events/EventsManagementPage.vue'),
        props: { mode: 'all' },
        meta: { roles: ['admin', 'sa'] },
      },
      {
        name: 'managed-events',
        path: 'managed-events',
        component: () => import('../pages/events/EventsManagementPage.vue'),
        props: { mode: 'managed' },
        meta: { roles: ['admin', 'sa', 'organiser'] },
      },
      {
        name: 'organiser-help',
        path: 'organiser-help',
        component: RouteViewComponent,
        redirect: { name: 'organiser-help-index' },
        meta: { roles: ['organiser', 'admin', 'sa'] },
        children: [
          {
            name: 'organiser-help-index',
            path: '',
            component: () => import('../pages/organiser-help/OrganiserHelpPage.vue'),
          },
          {
            name: 'streaming-help',
            path: 'streaming',
            component: () => import('../pages/organiser-help/StreamingHelpPage.vue'),
          },
        ],
      },
      {
        name: 'about',
        path: 'about',
        component: () => import('../pages/about/AboutPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
        meta: { roles: ['admin'] }, // only accissible to admin
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'registrate',
        path: 'registrate',
        component: () => import('../pages/auth/Registrate.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'lostpassword',
        path: 'lostpassword',
        component: () => import('../pages/auth/LostPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: '403',
    path: '/403',
    component: () => import('../pages/403.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  // Controleer of de route een beperking heeft op basis van rollen
  if (to.meta && to.meta.roles && Array.isArray(to.meta.roles)) {
    const userStore = useUserStore()
    await userStore.retrieveUserinfo()
    const userRole = userStore.role // Haal de huidige gebruikersrol op

    if (!to.meta.roles.includes(userRole)) {
      // Gebruiker heeft geen toegang, doorsturen naar Unauthorized-pagina

      return next({ name: '403' })
    }
  }

  // Ga door naar de volgende route
  next()
})

export default router
